/**
 * OpportunityDetail — full-CRM side drawer.
 *
 * Tabbed surface (Overview / Contacts / Activities / Tasks / Files / Lost)
 * matching international CRM standards (HubSpot/Salesforce/Pipedrive
 * equivalent for a holding company use-case).
 *
 * Receives an opportunity object + onChange callback to bubble updates
 * back to the parent list.  All mutations go through the
 * `/api/opportunities/{id}/...` REST sub-resources.
 */
import React, { useState, useEffect } from 'react';
import {
  X, Plus, Phone, Mail, Calendar as CalIcon, FileText, CheckCircle2, Trash2,
  Users, ListTodo, Activity as ActivityIcon, Paperclip, AlertOctagon, Edit3,
  Building2, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import LinkedInButton from '@/components/LinkedInButton';
import LinkedInAutofillField from '@/components/LinkedInAutofillField';
import PhoneInput from '@/components/PhoneInput';
import Avatar from '@/components/Avatar';

const STAGES = [
  { id: 'inbound',        label: 'Inbound',        color: '#94A3B8' },
  { id: 'triage',         label: 'Triage',         color: '#64748B' },
  { id: 'due_diligence',  label: 'Due Diligence',  color: '#0EA5E9' },
  { id: 'term_sheet',     label: 'Term Sheet',     color: '#8B3FC5' },
  { id: 'approved',       label: 'Approved',       color: '#5E1F8B' },
  { id: 'executed',       label: 'Executed',       color: '#0A0A0A' },
  { id: 'delivered',      label: 'Delivered',      color: '#059669' },
  { id: 'closed_lost',    label: 'Closed Lost',    color: '#EF4444' },
];

const LOST_REASONS = [
  { id: 'price',        label: 'Price' },
  { id: 'fit',          label: 'Product/strategic fit' },
  { id: 'timing',       label: 'Timing' },
  { id: 'competition',  label: 'Competition won' },
  { id: 'no_decision',  label: 'No decision' },
  { id: 'no_budget',    label: 'No budget' },
  { id: 'ghosted',      label: 'Ghosted' },
  { id: 'other',        label: 'Other' },
];

const ACTIVITY_TYPES = [
  { id: 'call',    label: 'Call',    Icon: Phone },
  { id: 'meeting', label: 'Meeting', Icon: Users },
  { id: 'email',   label: 'Email',   Icon: Mail },
  { id: 'note',    label: 'Note',    Icon: FileText },
];

function fmtMoney(n) {
  if (n == null) return null;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toString();
}

function fmtDateTime(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}

export default function OpportunityDetail({ opp, onClose, onChange }) {
  const [tab, setTab] = useState('overview');
  const [busy, setBusy] = useState(false);

  // Local mutable state — kept in sync with onChange after each save
  const reload = async () => {
    try {
      const { data } = await api.get(`/opportunities/${opp.opportunity_id}`);
      onChange && onChange(data);
    } catch (err) {
      // Parent list refresh will surface the error naturally.
      console.warn('OpportunityDetail: silent action failed', err);
    }
  };

  const moveStage = async (stage) => {
    if (stage === 'closed_lost') {
      // Don't allow direct lost-move — must go through Lost tab w/ reason
      setTab('lost');
      return;
    }
    setBusy(true);
    try {
      await api.post('/opportunities/stage', { opportunity_id: opp.opportunity_id, stage });
      toast.success(`Moved to ${stage.replace(/_/g, ' ')}`);
      reload();
    } catch { toast.error('Stage move failed'); }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="opp-detail-drawer"
    >
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        {/* HEADER */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{opp.type}</span>
                {opp.probability != null && (
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] bg-[#FFF8E5] px-1.5 py-0.5 rounded border border-[#8B3FC5]/40">
                    {opp.probability}% to win
                  </span>
                )}
              </div>
              <h2 className="display text-xl font-bold mt-1 leading-tight">{opp.title}</h2>
              <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                <Building2 size={13}/>
                <span>{opp.counterparty_name}</span>
                <LinkedInButton url={opp.counterparty_linkedin_url}/>
                {opp.deal_size != null && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="font-mono font-bold text-[#5E1F8B]">{fmtMoney(opp.deal_size)} {opp.currency}</span>
                  </>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="opp-detail-close">
              <X size={18}/>
            </button>
          </div>

          {/* Stage selector */}
          <div className="mt-5 flex flex-wrap gap-1.5" data-testid="opp-detail-stages">
            {STAGES.map((s) => (
              <button
                key={s.id}
                onClick={() => moveStage(s.id)}
                disabled={busy || opp.stage === s.id}
                data-testid={`opp-stage-${s.id}`}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border transition-colors disabled:opacity-100 ${
                  opp.stage === s.id
                    ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5] hover:text-[#5E1F8B]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Tabs */}
          <div className="mt-5 flex gap-1 border-b border-slate-100 -mx-6 px-6 overflow-x-auto" data-testid="opp-detail-tabs">
            {[
              ['overview',   'Overview',   Edit3],
              ['contacts',   `Contacts · ${(opp.contacts || []).length}`, Users],
              ['activities', `Activity · ${(opp.activities || []).length}`, ActivityIcon],
              ['tasks',      `Tasks · ${(opp.tasks || []).filter(t => t.status === 'open').length}`, ListTodo],
              ['files',      `Files · ${(opp.files || []).length}`, Paperclip],
              ['lost',       'Lost reason', AlertOctagon],
            ].map(([id, label, Icon]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`opp-tab-${id}`}
                className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${
                  tab === id
                    ? 'border-[#8B3FC5] text-[#5E1F8B]'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon size={11}/> {label}
              </button>
            ))}
          </div>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-5">
          {tab === 'overview'   && <OverviewTab opp={opp} reload={reload}/>}
          {tab === 'contacts'   && <ContactsTab opp={opp} reload={reload}/>}
          {tab === 'activities' && <ActivitiesTab opp={opp} reload={reload}/>}
          {tab === 'tasks'      && <TasksTab opp={opp} reload={reload}/>}
          {tab === 'files'      && <FilesTab opp={opp} reload={reload}/>}
          {tab === 'lost'       && <LostTab opp={opp} reload={reload}/>}
        </div>
      </div>
    </div>
  );
}

// ───────────── OVERVIEW ─────────────
function OverviewTab({ opp, reload }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 text-sm">
        <Field label="Counterparty" value={opp.counterparty_name}>
          <LinkedInButton url={opp.counterparty_linkedin_url}/>
        </Field>
        <Field label="Sector" value={opp.sector || '—'}/>
        <Field label="Source" value={
          opp.source_category
            ? `${opp.source_category}${opp.source_detail ? ` · ${opp.source_detail}` : ''}`
            : (opp.source || '—')
        }/>
        <Field label="Expected close" value={fmtDate(opp.expected_close_date)}/>
        <Field label="Probability" value={`${opp.probability ?? 0}%`}/>
        <Field label="Stage" value={(opp.stage || '').replace(/_/g, ' ')}/>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Description</div>
        <p className="mt-1.5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{opp.description}</p>
      </div>
    </div>
  );
}

function Field({ label, value, children }) {
  return (
    <div>
      <span className="text-slate-500 uppercase text-[10px] tracking-[0.18em] font-bold block">{label}</span>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span>{value}</span>
        {children}
      </div>
    </div>
  );
}

// ───────────── CONTACTS ─────────────
function ContactsTab({ opp, reload }) {
  const [adding, setAdding] = useState(false);
  const blank = {
    name: '', email: '', phone: '', phone_country: null, role: '',
    linkedin_url: '', picture: null, is_primary: false,
  };
  const [form, setForm] = useState(blank);

  const handleAutofill = (fields) => {
    setForm((prev) => ({
      ...prev,
      name: prev.name || fields.name || '',
      role: prev.role || fields.current_title || '',
      picture: prev.picture || fields.picture_url || null,
    }));
  };

  const save = async () => {
    // Backend requires email + phone — surface the same rule client-side so
    // the user gets an immediate inline error instead of a 422 round-trip.
    if (!form.name.trim()) return toast.error('Contact name is required.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      return toast.error('A valid contact email is required.');
    const digits = (form.phone || '').replace(/[^\d]/g, '');
    if (digits.length < 7)
      return toast.error('A valid contact phone is required (min 7 digits).');
    try {
      await api.post(`/opportunities/${opp.opportunity_id}/contacts`, form);
      toast.success('Contact added.');
      setForm(blank);
      setAdding(false);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };

  const setPrimary = async (contactId) => {
    try {
      await api.patch(`/opportunities/${opp.opportunity_id}/contacts/${contactId}`,
        { is_primary: true });
      toast.success('Primary contact updated.');
      reload();
    } catch { toast.error('Failed.'); }
  };

  const remove = async (contactId) => {
    if (!confirm('Remove this contact?')) return;
    try {
      await api.delete(`/opportunities/${opp.opportunity_id}/contacts/${contactId}`);
      reload();
    } catch { toast.error('Failed.'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="display font-bold">Contacts</h3>
        {!adding && (
          <button onClick={() => setAdding(true)} className="ve-btn-secondary text-xs"
                  data-testid="contact-add-btn">
            <Plus size={11}/> Add contact
          </button>
        )}
      </div>

      {adding && (
        <div className="ve-card p-4 space-y-3 border-[#8B3FC5]" data-testid="contact-add-form">
          <LinkedInAutofillField
            value={form.linkedin_url}
            onChange={(v) => setForm({ ...form, linkedin_url: v })}
            onAutofill={handleAutofill}
            testId="contact-linkedin"
          />
          <div className="flex items-center gap-3">
            <Avatar
              src={form.picture}
              email={form.email}
              name={form.name || 'Contact'}
              size={48}
              testId="contact-avatar-preview"
            />
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 leading-snug">
              Avatar is pulled from LinkedIn when available, then falls back to Gravatar, then to initials.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <input placeholder="Full name *" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                   required data-testid="contact-name"
                   className="rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#8B3FC5]"/>
            <input placeholder="Role / title" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
                   className="rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#8B3FC5]"/>
            <input type="email" placeholder="Email *" required value={form.email}
                   onChange={(e) => setForm({...form, email: e.target.value})}
                   data-testid="contact-email"
                   className="rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#8B3FC5]"/>
            <PhoneInput
              value={form.phone}
              country={form.phone_country}
              required
              onChange={(phone, iso) => setForm({ ...form, phone, phone_country: iso })}
              testId="contact-phone"
            />
          </div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Email <b>and</b> phone are mandatory — needed for every outbound communication.
          </p>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({...form, is_primary: e.target.checked})}/>
            Set as primary contact
          </label>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setAdding(false); setForm(blank); }} className="ve-btn-secondary text-xs">Cancel</button>
            <button onClick={save} className="ve-btn-primary text-xs"
                    data-testid="contact-save-btn">Save contact</button>
          </div>
        </div>
      )}

      {(opp.contacts || []).length === 0 && !adding && (
        <div className="ve-card p-6 text-center text-sm text-slate-500" data-testid="contacts-empty">
          No contacts yet. Click <b>Add contact</b> — paste a LinkedIn URL and we&apos;ll prefill the name + title.
        </div>
      )}

      {(opp.contacts || []).map((c) => (
        <div key={c.contact_id} className="ve-card p-4" data-testid={`contact-row-${c.contact_id}`}>
          <div className="flex items-start justify-between gap-3">
            <Avatar
              src={c.picture}
              email={c.email}
              name={c.name}
              size={44}
              testId={`contact-avatar-${c.contact_id}`}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-bold">{c.name}</h4>
                <LinkedInButton url={c.linkedin_url}/>
                {c.is_primary && (
                  <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] bg-[#FFF8E5] px-1.5 py-0.5 rounded border border-[#8B3FC5]/40">
                    Primary
                  </span>
                )}
              </div>
              {c.role && <div className="text-xs text-slate-500 mt-0.5">{c.role}</div>}
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-[#5E1F8B]"><Mail size={11}/> {c.email}</a>}
                {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-[#5E1F8B]"><Phone size={11}/> {c.phone}</a>}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {!c.is_primary && (
                <button onClick={() => setPrimary(c.contact_id)}
                        className="text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded border border-slate-200 hover:border-[#8B3FC5] hover:text-[#5E1F8B]"
                        title="Set as primary contact">
                  Make primary
                </button>
              )}
              <button onClick={() => remove(c.contact_id)}
                      className="text-rose-400 hover:text-rose-600 p-1"
                      title="Remove contact">
                <Trash2 size={12}/>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ───────────── ACTIVITIES ─────────────
function ActivitiesTab({ opp, reload }) {
  const [type, setType] = useState('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [duration, setDuration] = useState('');

  const log = async () => {
    if (!title.trim()) return toast.error('Activity title required.');
    try {
      await api.post(`/opportunities/${opp.opportunity_id}/activities`, {
        type, title, body: body || null,
        duration_minutes: duration ? parseInt(duration) : null,
        logged_by_user_id: 'self',  // backend overrides
      });
      setTitle(''); setBody(''); setDuration('');
      toast.success('Activity logged.');
      reload();
    } catch { toast.error('Failed.'); }
  };

  return (
    <div className="space-y-4">
      <div className="ve-card p-4 space-y-3" data-testid="activity-log-form">
        <div className="flex gap-1 flex-wrap">
          {ACTIVITY_TYPES.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setType(id)}
              data-testid={`activity-type-${id}`}
              className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded border inline-flex items-center gap-1 transition-colors ${
                type === id
                  ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'
              }`}
            >
              <Icon size={10}/> {label}
            </button>
          ))}
        </div>
        <input
          placeholder={`What ${type} are you logging?`}
          value={title} onChange={(e) => setTitle(e.target.value)}
          data-testid="activity-title-input"
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
        />
        <textarea
          rows={2}
          placeholder="Notes (optional)"
          value={body} onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
        />
        <div className="flex items-center gap-2">
          {(type === 'call' || type === 'meeting') && (
            <input
              type="number" min="1" placeholder="Duration (mins)"
              value={duration} onChange={(e) => setDuration(e.target.value)}
              className="w-32 rounded-md border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:border-[#8B3FC5]"
            />
          )}
          <button onClick={log} className="ve-btn-primary text-xs ml-auto"
                  data-testid="activity-log-btn">
            <Send size={11}/> Log {type}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {(opp.activities || []).length === 0 && (
          <div className="ve-card p-6 text-center text-sm text-slate-500">
            No activities yet — log calls, emails and meetings here to build a deal-history audit trail.
          </div>
        )}
        {(opp.activities || []).map((a) => {
          const meta = ACTIVITY_TYPES.find((t) => t.id === a.type);
          const Icon = meta?.Icon || FileText;
          return (
            <div key={a.activity_id} className="ve-card p-3" data-testid={`activity-${a.activity_id}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className="h-7 w-7 rounded-full bg-[#FFF8E5] text-[#5E1F8B] flex items-center justify-center shrink-0">
                  <Icon size={12}/>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{a.title}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {a.logged_by_name || 'unknown'} · {fmtDateTime(a.occurred_at)}
                    {a.duration_minutes && ` · ${a.duration_minutes}m`}
                  </div>
                </div>
              </div>
              {a.body && <p className="text-xs text-slate-700 mt-2 pl-9 leading-relaxed">{a.body}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────────── TASKS ─────────────
function TasksTab({ opp, reload }) {
  const blank = { title: '', description: '', due_date: '' };
  const [form, setForm] = useState(blank);

  const create = async () => {
    if (!form.title.trim()) return toast.error('Task title required.');
    try {
      await api.post(`/opportunities/${opp.opportunity_id}/tasks`, {
        title: form.title,
        description: form.description || null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        created_by_user_id: 'self',
      });
      setForm(blank);
      toast.success('Task added.');
      reload();
    } catch { toast.error('Failed.'); }
  };

  const toggle = async (task) => {
    const next = task.status === 'open' ? 'done' : 'open';
    try {
      await api.patch(`/opportunities/${opp.opportunity_id}/tasks/${task.task_id}`,
        { status: next });
      reload();
    } catch { toast.error('Failed.'); }
  };

  const remove = async (taskId) => {
    if (!confirm('Delete this task?')) return;
    try {
      await api.delete(`/opportunities/${opp.opportunity_id}/tasks/${taskId}`);
      reload();
    } catch { toast.error('Failed.'); }
  };

  const tasks = opp.tasks || [];
  const open = tasks.filter((t) => t.status === 'open');
  const done = tasks.filter((t) => t.status === 'done');

  return (
    <div className="space-y-4">
      <div className="ve-card p-4 space-y-2" data-testid="task-create-form">
        <input
          placeholder="What needs to happen next?"
          value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
          data-testid="task-title-input"
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
        />
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:border-[#8B3FC5]"
            placeholder="Due date"
          />
          <button onClick={create} className="ve-btn-primary text-xs ml-auto" data-testid="task-create-btn">
            <Plus size={11}/> Add task
          </button>
        </div>
      </div>

      {open.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
            Open · {open.length}
          </div>
          <div className="space-y-2">
            {open.map((t) => (
              <TaskRow key={t.task_id} task={t} onToggle={() => toggle(t)} onRemove={() => remove(t.task_id)}/>
            ))}
          </div>
        </div>
      )}

      {done.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
            Completed · {done.length}
          </div>
          <div className="space-y-2 opacity-60">
            {done.map((t) => (
              <TaskRow key={t.task_id} task={t} onToggle={() => toggle(t)} onRemove={() => remove(t.task_id)}/>
            ))}
          </div>
        </div>
      )}

      {tasks.length === 0 && (
        <div className="ve-card p-6 text-center text-sm text-slate-500" data-testid="tasks-empty">
          No tasks yet — add next steps so the deal stays moving.
        </div>
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onRemove }) {
  const overdue = task.status === 'open' && task.due_date &&
    new Date(task.due_date) < new Date();
  return (
    <div className={`ve-card p-3 flex items-center gap-3 ${overdue ? 'border-rose-300' : ''}`}
         data-testid={`task-${task.task_id}`}>
      <button
        onClick={onToggle}
        className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
          task.status === 'done'
            ? 'bg-emerald-500 border-emerald-500 text-white'
            : 'border-slate-300 hover:border-[#8B3FC5]'
        }`}
        title={task.status === 'open' ? 'Mark as done' : 'Re-open'}
        data-testid={`task-toggle-${task.task_id}`}
      >
        {task.status === 'done' && <CheckCircle2 size={12}/>}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold ${task.status === 'done' ? 'line-through' : ''}`}>
          {task.title}
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
          {task.due_date && (
            <span className={overdue ? 'text-rose-700 font-bold' : ''}>
              Due {fmtDate(task.due_date)}
            </span>
          )}
          {task.assignee_name && (
            <span> · {task.assignee_name}</span>
          )}
        </div>
      </div>
      <button onClick={onRemove} className="text-rose-400 hover:text-rose-600 p-1">
        <Trash2 size={12}/>
      </button>
    </div>
  );
}

// ───────────── FILES ─────────────
function FilesTab({ opp, reload }) {
  const blank = { name: '', url: '' };
  const [form, setForm] = useState(blank);

  const add = async () => {
    if (!form.name.trim() || !form.url.trim()) return toast.error('Name + URL required.');
    try {
      await api.post(`/opportunities/${opp.opportunity_id}/files`, {
        ...form, uploaded_by_user_id: 'self',
      });
      setForm(blank);
      toast.success('File attached.');
      reload();
    } catch { toast.error('Failed.'); }
  };

  const remove = async (fileId) => {
    if (!confirm('Remove this file link?')) return;
    try {
      await api.delete(`/opportunities/${opp.opportunity_id}/files/${fileId}`);
      reload();
    } catch { toast.error('Failed.'); }
  };

  return (
    <div className="space-y-4">
      <div className="ve-card p-4 space-y-2" data-testid="file-add-form">
        <input
          placeholder="File name (e.g. 'Term Sheet v2.pdf')"
          value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
        />
        <div className="flex gap-2">
          <input
            placeholder="URL (paste a Drive / Dropbox / SharePoint link)"
            value={form.url} onChange={(e) => setForm({...form, url: e.target.value})}
            className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
          />
          <button onClick={add} className="ve-btn-primary text-xs" data-testid="file-add-btn">
            <Plus size={11}/> Attach
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {(opp.files || []).length === 0 && (
          <div className="ve-card p-6 text-center text-sm text-slate-500">
            No files yet — attach term sheets, NDAs, decks, and contracts here for the deal record.
          </div>
        )}
        {(opp.files || []).map((f) => (
          <div key={f.file_id} className="ve-card p-3 flex items-center gap-3" data-testid={`file-${f.file_id}`}>
            <Paperclip size={16} className="text-[#5E1F8B] shrink-0"/>
            <div className="flex-1 min-w-0">
              <a href={f.url} target="_blank" rel="noreferrer noopener"
                 className="font-bold text-sm hover:text-[#5E1F8B] truncate block">
                {f.name}
              </a>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
                Uploaded {fmtDateTime(f.uploaded_at)}
              </div>
            </div>
            <button onClick={() => remove(f.file_id)} className="text-rose-400 hover:text-rose-600 p-1">
              <Trash2 size={12}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────── LOST REASON ─────────────
function LostTab({ opp, reload }) {
  const [reason, setReason] = useState(opp.lost_reason || '');
  const [note, setNote] = useState(opp.lost_reason_note || '');

  const save = async () => {
    if (!reason) return toast.error('Pick a reason.');
    try {
      await api.post(`/opportunities/${opp.opportunity_id}/lost`, { reason, note: note || null });
      toast.success('Opportunity marked lost.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };

  const isAlreadyLost = opp.stage === 'closed_lost';
  return (
    <div className="space-y-4">
      <div className="ve-card p-4 border-rose-200">
        <div className="flex items-center gap-2">
          <AlertOctagon size={16} className="text-rose-600"/>
          <h3 className="display font-bold">{isAlreadyLost ? 'Marked as lost' : 'Mark this opportunity as lost'}</h3>
        </div>
        <p className="text-xs text-slate-600 mt-1">
          A taxonomic reason is required so we can improve win-rates over time.
          {isAlreadyLost && ' You can update the reason or note below.'}
        </p>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="lost-reason-grid">
          {LOST_REASONS.map((r) => (
            <button
              key={r.id}
              onClick={() => setReason(r.id)}
              data-testid={`lost-reason-${r.id}`}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold rounded border transition-colors ${
                reason === r.id
                  ? 'bg-rose-500 text-white border-rose-500'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-rose-300'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <textarea
          rows={3}
          placeholder="Notes (optional — 'They wanted 20% lower valuation', 'Won by Vendor X', …)"
          value={note} onChange={(e) => setNote(e.target.value)}
          data-testid="lost-note"
          className="mt-3 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-rose-300"
        />

        <div className="mt-3 flex justify-end">
          <button onClick={save} className="ve-btn-primary text-xs bg-rose-500 border-rose-500 text-white hover:bg-rose-600"
                  data-testid="lost-save-btn">
            <AlertOctagon size={11}/> {isAlreadyLost ? 'Update lost reason' : 'Mark lost'}
          </button>
        </div>
      </div>
    </div>
  );
}
